import { SettingsTypes, SeatSettings } from './types';
import { searchSettings } from './search/reducers';
import { webRecordingSettingsReducer as webRecording } from './webRecording/reducers';
import { mobileRecordingSettingsReducer as mobileRecording } from './mobileRecording/reducers';
import { profileSettingsReducer as profile } from './profile/reducers';
import { DismissibleWarning, InactivityTimeoutSettings } from '@fullstory/client-api';
import { SettingsResponse, TeamSettings } from '@fullstory/proto/fs/api/clientapi';
import { SettingsActions } from './actions';
import {
  ApiKeyMetadata,
  Invite,
  PrivacySettingsResponse,
  UpdateAction,
  GetSeatsAndInvitesResponse,
  SeatID,
  Seat,
  SeatOrInvite,
  BatchUpdateOrgSeatsRequest,
  Role,
  AnalyzeQuotaResponse,
  SessionOverageConfig,
  NotificationConfig,
  CustomSeatRequestConfig,
} from '@fullstory/proto/fs/services/orgs';
import { createLoadingReducer, createRequestReducer } from '@fullstory/redux-util';
import { combineReducers } from '@fullstory/redux-util/utils';
import { contactsReducer as contacts } from './contacts';
import { integrationsReducer as integrations } from './integrations';
import { dataExport } from './dataExport';
import { ssoReducer as sso } from './sso';
import { billingReducer as billing } from './billing';
import { umbrellaReducer as umbrella } from './umbrella/reducers';
import { FullstoryActions } from '../fullstory/actions';
import { FullstoryTypes } from '../fullstory/types';
import { warehouseSettings } from './warehouse';
import { signalsReducer as signals } from './signals';
import { dismissibleWarningProtoToDismissibleWarningModel } from './utils';
import { experimentConfigReducer as experimentConfig } from './experiments/reducers';

const settings = createRequestReducer<SettingsResponse>()(
  SettingsTypes.FetchSettingsRequest,
  SettingsTypes.FetchSettingsSuccess,
  SettingsTypes.FetchSettingsFailure,
);

const teamSettings = (
  state: TeamSettings | null = null,
  action: SettingsActions,
): TeamSettings | null => {
  switch (action.type) {
    case SettingsTypes.FetchTeamSettingsSuccess:
      return action.data;
    case SettingsTypes.UpdateDomainsSuccess:
      return state === null
        ? state
        : {
            ...state,
            shareEmailDomains: action.data,
          };
    case SettingsTypes.UpdateJoinOrgSettingsSuccess: {
      const update = action.data;
      return state === null
        ? state
        : {
            ...state,
            joinOrgEnabled:
              update.joinOrgEnabled === undefined ? state.joinOrgEnabled : update.joinOrgEnabled,
            joinOrgDomains: update.domains === undefined ? state.joinOrgDomains : update.domains,
          };
    }
    case SettingsTypes.SendInviteSuccess: {
      if (state === null || state.seatsAndInvites === null) return state;

      const invites = state.seatsAndInvites.invites;

      // If it's a resend, update the invite with the new timestamp
      const existingInviteIndex = invites.findIndex(
        invite => invite !== null && invite.id?.email === action.data.id?.email,
      );
      if (existingInviteIndex !== -1) {
        return {
          ...state,
          seatsAndInvites: {
            ...state.seatsAndInvites,
            invites: [
              ...invites.slice(0, existingInviteIndex),
              action.data,
              ...invites.slice(existingInviteIndex + 1),
            ],
          },
        };
      } else {
        return {
          ...state,
          seatsAndInvites: {
            ...state.seatsAndInvites,
            invites: [...invites, action.data],
          },
        };
      }
    }
    case SettingsTypes.RevokeInviteSuccess:
      if (state === null || state.seatsAndInvites === null) return state;

      return {
        ...state,
        seatsAndInvites: {
          ...state.seatsAndInvites,
          invites: state.seatsAndInvites.invites.filter(invite => invite.id?.email !== action.data),
        },
      };
    case SettingsTypes.UpdateSeatSuccess: {
      if (state === null || state.seatsAndInvites === null) return state;
      const { SeatRole, Email } = action.request.body!;
      if (SeatRole === undefined) {
        return {
          ...state,
          seatsAndInvites: {
            ...state.seatsAndInvites,
            seats: state.seatsAndInvites.seats.filter(seat => seat.id?.email !== Email),
          },
        };
      } else {
        return {
          ...state,
          seatsAndInvites: {
            ...state.seatsAndInvites,
            seats: state.seatsAndInvites.seats.map(seat => {
              if (seat.id?.email === Email) {
                seat.role = SeatRole;
              }
              return seat;
            }),
          },
        };
      }
    }
    case SettingsTypes.BatchDeleteSeatsSuccess: {
      if (state === null || state.seatsAndInvites === null || !action.request.body) return state;
      const { emails } = action.request.body;
      return {
        ...state,
        seatsAndInvites: {
          ...state.seatsAndInvites,
          seats: state.seatsAndInvites.seats.filter(
            seat => seat.id && !emails.includes(seat.id.email),
          ),
        },
      };
    }
    case SettingsTypes.UpdateSSOAuthSuccess:
    case SettingsTypes.UpdateGoogleAuthSuccess:
      if (state === null) return state;

      return {
        ...state,
        idProviderEnforced: action.request.body!.Enforced,
      };
  }
  return state;
};

const teamSettingsLoading = createLoadingReducer(
  SettingsTypes.FetchTeamSettingsRequest,
  SettingsTypes.FetchTeamSettingsSuccess,
  SettingsTypes.FetchTeamSettingsFailure,
);

const inactivityTimeoutSettings = createRequestReducer<InactivityTimeoutSettings>()(
  SettingsTypes.FetchInactivityTimeoutSettingsRequest,
  SettingsTypes.FetchInactivityTimeoutSettingsSuccess,
  SettingsTypes.FetchInactivityTimeoutSettingsFailure,
);

const invites = createRequestReducer<Invite[], Invite>({
  mergeData: (addedInvites, newestInvite) => [newestInvite].concat(addedInvites || []),
})(
  SettingsTypes.SendInviteRequest,
  SettingsTypes.SendInviteSuccess,
  SettingsTypes.SendInviteFailure,
);

const initialSeatSettings: Partial<SeatSettings> = {
  DigestFreq: undefined,
  QuotaEmailEnabled: undefined,
  WebhookNotificationsEnabled: undefined,
  MentionEmailsEnabled: undefined,
  DismissedWarnings: undefined,
  OpportunitiesEmailsEnabled: undefined,
};

const seatSettings = (
  state: Partial<SeatSettings> = initialSeatSettings,
  action: SettingsActions | FullstoryActions,
): Partial<SeatSettings> => {
  switch (action.type) {
    case FullstoryTypes.Bootstrap:
      return {
        ...state,
        ...action.bootstrap.SeatSettings,
      };
    case SettingsTypes.UpdateSeatSettingsSuccess:
      return {
        ...state,
        ...action.request.body,
      };
    case SettingsTypes.UpdateDismissibleWarningSuccess: {
      const dismissedWarning = dismissibleWarningProtoToDismissibleWarningModel(
        action.request.body?.warning,
      );
      if (
        !dismissedWarning ||
        dismissedWarning.valueOf() === DismissibleWarning.WarningUnknown.valueOf()
      ) {
        return state;
      }

      if (state.DismissedWarnings && state.DismissedWarnings.includes(dismissedWarning)) {
        return state;
      } else if (state.DismissedWarnings) {
        return {
          ...state,
          DismissedWarnings: Array.from(new Set([...state.DismissedWarnings, dismissedWarning])),
        };
      } else {
        return {
          ...state,
          DismissedWarnings: [dismissedWarning],
        };
      }
    }
  }
  return state;
};

const updateSeat = (
  seatId: Partial<SeatID>,
  seatData: Partial<Seat>,
  users: SeatOrInvite[],
): SeatOrInvite[] => {
  const index = users.findIndex(entity => entity.seat?.id?.email === seatId.email);
  if (index === -1) {
    return users;
  }
  const toUpdate = users[index];
  if (!toUpdate?.seat) {
    return users;
  }
  return [
    ...users.slice(0, index),
    {
      ...toUpdate,
      seat: { ...toUpdate.seat, ...seatData },
    },
    ...users.slice(index + 1),
  ];
};

const updateSeats = (
  updates: { [key: string]: BatchUpdateOrgSeatsRequest.Update },
  users: SeatOrInvite[],
): SeatOrInvite[] => {
  const updatedUsers = [...users];
  Object.entries(updates).forEach(([email, update]) => {
    const userIndex = updatedUsers.findIndex(entity => entity.seat?.id?.email === email);
    const userToUpdate = updatedUsers[userIndex];
    if (!userToUpdate?.seat) return;

    if (update.role !== Role.UNKNOWN) {
      userToUpdate.seat.role = update.role;
    }
    if (update.status !== BatchUpdateOrgSeatsRequest.Status.UNSET) {
      userToUpdate.seat.disabled = update.status === BatchUpdateOrgSeatsRequest.Status.DISABLED;
    }
  });
  return updatedUsers;
};

// Flag to track the initial load of seatsInvites, allows us to postpone rendering the
// users table until the entities are populated
const seatsInvitesInitialized = (initialized = false, action: SettingsActions) =>
  initialized || action.type === SettingsTypes.FetchSeatsInvitesSuccess;
const initialSeatsInvites: GetSeatsAndInvitesResponse = {
  entities: [],
  domains: [],
  totalCount: 0,
};
const seatsInvites = (
  state: GetSeatsAndInvitesResponse = initialSeatsInvites,
  action: SettingsActions,
): GetSeatsAndInvitesResponse => {
  switch (action.type) {
    case SettingsTypes.FetchSeatsInvitesSuccess:
      return action.data;
    case SettingsTypes.UpdateSeatDisabledSuccess: {
      const req = action.request.body!;
      if (req.seatId) {
        return {
          ...state,
          entities: updateSeat(req.seatId, { disabled: req.disabled }, state.entities),
        };
      }
      break;
    }
    case SettingsTypes.UpdateSeatSuccess: {
      const req = action.request.body!;
      if (req.Email) {
        return {
          ...state,
          entities: updateSeat({ email: req.Email }, { role: req.SeatRole }, state.entities),
        };
      }
      break;
    }
    case SettingsTypes.BatchUpdateSeatsSuccess: {
      const req = action.request.body!;
      const newEntities = updateSeats(req.updates, state.entities);
      return {
        ...state,
        entities: newEntities,
      };
    }
    case SettingsTypes.BatchRevokeInvitesSuccess: {
      const emails = action.request.body?.emails || [];
      const entities = [...state.entities];
      emails.forEach((email: string) => {
        const index = entities.findIndex(
          (entity: SeatOrInvite) => entity.invite?.id?.email === email,
        );
        if (index !== -1) {
          entities.splice(index, 1);
        }
      });
      return {
        ...state,
        entities,
      };
    }
    case SettingsTypes.RevokeInviteSuccess: {
      const email = action.data;
      const entities = [...state.entities];
      const index = entities.findIndex(
        (entity: SeatOrInvite) => entity.invite?.id?.email === email,
      );
      if (index !== -1) {
        entities.splice(index, 1);
      }
      return {
        ...state,
        entities,
      };
    }
    case SettingsTypes.SendInvitesSuccess: {
      const entities = [...state.entities];
      action.data.invites.forEach((invite: Invite) => {
        const index = entities.findIndex(
          (entity: SeatOrInvite) => entity.invite?.id?.email === invite.id?.email,
        );
        if (index === -1) {
          entities.push({ invite, groups: entities[index]?.groups || [] });
        } else {
          entities[index] = { invite, groups: entities[index]?.groups || [] };
        }
      });

      return {
        ...state,
        entities,
      };
    }
  }
  return state;
};

const guests = (
  state: GetSeatsAndInvitesResponse = initialSeatsInvites,
  action: SettingsActions,
): GetSeatsAndInvitesResponse => {
  switch (action.type) {
    case SettingsTypes.FetchGuestsSuccess: {
      const domains = new Set<string>();
      const entities = action.data.entities;
      entities.forEach(entity => {
        const email = entity.seat?.id?.email;
        if (email) {
          const idx = email.indexOf('@');
          domains.add(email.substring(idx + 1));
        }
      });
      return { ...state, domains: [...domains], entities };
    }
    case SettingsTypes.UpdateSeatSuccess: {
      const req = action.request.body!;
      if (req.Email) {
        return {
          ...state,
          entities: updateSeat({ email: req.Email }, { role: req.SeatRole }, state.entities),
        };
      }
      break;
    }
  }
  return state;
};

const apiKeys = (state: ApiKeyMetadata[] = [], action: SettingsActions): ApiKeyMetadata[] => {
  switch (action.type) {
    case SettingsTypes.FetchApiKeysSuccess:
      return action.data.keys ?? [];
    case SettingsTypes.DeleteApiKeySuccess: {
      const delReq = action.request.body!;
      return state.filter(k => k.seatId != delReq.seatId || k.suffix != delReq.suffix);
    }
    case SettingsTypes.CreateApiKeySuccess:
      return [...state, action.data.meta];
    case SettingsTypes.UpdateApiKeySuccess: {
      const updateReq = action.request.body!;
      const prevMetadata = state.find(k => k.suffix === updateReq.suffix);
      if (!prevMetadata) return state;
      const index = state.indexOf(prevMetadata);
      const updatedMetadata: ApiKeyMetadata = {
        ...prevMetadata,
        name: updateReq.newName,
        role: updateReq.newRole,
      };
      const updatedState = [...state];
      updatedState.splice(index, 1, updatedMetadata);
      return updatedState;
    }
  }
  return state;
};

const initialPrivacySettings: PrivacySettingsResponse = {
  elementBlocks: [],
  ajaxWatches: [],
  discardIpAddresses: true,
  privateByDefault: false,
  elementRuleGroups: {},
  urlPrivacyRules: [],
};

const privacySettings = (
  state: PrivacySettingsResponse = initialPrivacySettings,
  action: SettingsActions,
): PrivacySettingsResponse => {
  switch (action.type) {
    case SettingsTypes.FetchPrivacySettingsSuccess:
      return action.data;
    case SettingsTypes.UpdateDiscardIpAddrsSuccess:
      return action.request.body == null
        ? state
        : { ...state, discardIpAddresses: action.request.body.discardIpAddresses };
    case SettingsTypes.UpdateElementRecordingRulesSuccess: {
      if (action.request.body == null) return state;
      const elementBlocksCopy = [...state.elementBlocks];
      action.request.body.elementBlocks.forEach(update => {
        switch (update.action) {
          case UpdateAction.ADD:
            elementBlocksCopy.push({ ...update.block!, lastUpdated: action.data.updateTime });
            break;
          case UpdateAction.UPDATE: {
            const toUpdate = elementBlocksCopy.findIndex(
              e => e.selector === update.previousSelector,
            );
            if (toUpdate !== -1) {
              elementBlocksCopy.splice(toUpdate, 1, {
                ...update.block!,
                lastUpdated: action.data.updateTime,
              });
            }
            break;
          }
          case UpdateAction.DELETE: {
            const toDelete = elementBlocksCopy.findIndex(
              e => e.selector === update.previousSelector,
            );
            if (toDelete !== -1) {
              elementBlocksCopy.splice(toDelete, 1);
            }
            break;
          }
        }
      });

      return { ...state, elementBlocks: elementBlocksCopy };
    }
    default:
      return state;
  }
};

const quotaAnalysis = createRequestReducer<AnalyzeQuotaResponse>()(
  SettingsTypes.AnalyzeQuotaRequest,
  SettingsTypes.AnalyzeQuotaSuccess,
  SettingsTypes.AnalyzeQuotaFailure,
);

const initialSessionOverageConfig: Partial<SessionOverageConfig> = {
  blockLimit: undefined,
  overageAllowed: undefined,
};

const sessionOverageConfig = (state = initialSessionOverageConfig, action: SettingsActions) => {
  switch (action.type) {
    case SettingsTypes.GetSessionOverageConfigSuccess:
      return action.data;
    case SettingsTypes.UpdateSessionOverageConfigSuccess:
      return action.data;
    default:
      return state;
  }
};

const initialNotificationConfig: Partial<NotificationConfig> = {
  quotaEmailsDisabled: undefined,
  nearQuotaEmailThreshold: undefined,
  dataCaptureStoppedDisabled: undefined,
};

const notificationConfig = (state = initialNotificationConfig, action: SettingsActions) => {
  switch (action.type) {
    case SettingsTypes.GetNotificationConfigSuccess:
      return action.data;
    case SettingsTypes.UpdateNotificationConfigSuccess:
      return action.data;
    default:
      return state;
  }
};

const snoozeSessionBannerNotification = (state = false, action: SettingsActions) => {
  switch (action.type) {
    case SettingsTypes.SnoozeSessionBannerNotification:
      return true;
    default:
      return state;
  }
};

const sessionManagement = combineReducers({
  quotaAnalysis,
  sessionOverageConfig,
  notificationConfig,
  snoozeSessionBannerNotification,
});

const initialCustomSeatRequestConfig: CustomSeatRequestConfig = {
  enabled: false,
  customSeatText: '',
  customSeatUrl: '',
};
const customSeatRequestSettings = (
  state = initialCustomSeatRequestConfig,
  action: SettingsActions,
) => {
  switch (action.type) {
    case SettingsTypes.GetCustomSeatRequestConfigSuccess:
      return action.data;
    case SettingsTypes.UpdateCustomSeatRequestConfigSuccess:
      return action.data;
    default:
      return state;
  }
};

type SettingsReducerState = {
  settings: ReturnType<typeof settings>;
  teamSettings: ReturnType<typeof teamSettings>;
  teamSettingsLoading: ReturnType<typeof teamSettingsLoading>;
  webRecording: ReturnType<typeof webRecording>;
  mobileRecording: ReturnType<typeof mobileRecording>;
  invites: ReturnType<typeof invites>;
  inactivityTimeoutSettings: ReturnType<typeof inactivityTimeoutSettings>;
  seatSettings: ReturnType<typeof seatSettings>;
  seatsInvites: ReturnType<typeof seatsInvites>;
  guests: ReturnType<typeof guests>;
  seatsInvitesInitialized: ReturnType<typeof seatsInvitesInitialized>;
  apiKeys: ReturnType<typeof apiKeys>;
  privacySettings: ReturnType<typeof privacySettings>;
  searchSettings: ReturnType<typeof searchSettings>;
  contacts: ReturnType<typeof contacts>;
  integrations: ReturnType<typeof integrations>;
  signals: ReturnType<typeof signals>;
  dataExport: ReturnType<typeof dataExport>;
  sso: ReturnType<typeof sso>;
  profile: ReturnType<typeof profile>;
  billing: ReturnType<typeof billing>;
  umbrella: ReturnType<typeof umbrella>;
  sessionManagement: ReturnType<typeof sessionManagement>;
  warehouseSettings: ReturnType<typeof warehouseSettings>;
  customSeatRequestSettings: ReturnType<typeof customSeatRequestSettings>;
  experimentConfig: ReturnType<typeof experimentConfig>;
};

export const settingsReducer = combineReducers<SettingsReducerState>({
  settings,
  teamSettings,
  teamSettingsLoading,
  webRecording,
  mobileRecording,
  invites,
  inactivityTimeoutSettings,
  seatSettings,
  seatsInvites,
  guests,
  seatsInvitesInitialized,
  apiKeys,
  privacySettings,
  searchSettings,
  contacts,
  integrations,
  dataExport,
  sso,
  signals,
  profile,
  billing,
  umbrella,
  sessionManagement,
  warehouseSettings,
  customSeatRequestSettings,
  experimentConfig,
});
